import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLingui } from "@lingui/react/macro";
import { ArrowRight, Mail, Plus } from "lucide-react";
import { orgRoleApi } from "@/lib/api/services/role.api";
import { useOnboarding } from "@/features/onboarding/OnboardingContext";
import { useAuth } from "@/hooks/useAuth";
import type { OnboardingInvite } from "@/features/onboarding/OnboardingContext";

export const Route = createFileRoute("/(public)/onboarding/invite")({
  component: InviteUsersPage,
});

interface InviteRow {
  email: string;
  roleId: string;
}

function toRows(invites: OnboardingInvite[]): InviteRow[] {
  if (invites.length > 0) {
    return invites.map((i) => ({ email: i.email, roleId: String(i.roleId) }));
  }
  return [{ email: "", roleId: "" }, { email: "", roleId: "" }, { email: "", roleId: "" }];
}

function InviteUsersPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { invites: savedInvites, setInvites } = useOnboarding();
  const { user } = useAuth();
  const [rows, setRows] = useState<InviteRow[]>(() => toRows(savedInvites));

  // A placeholder org is created at sign-up and the user is ORG_ADMIN on it, so
  // org roles are fetchable throughout onboarding (the org exists before setup
  // completes). Guard on org presence only as a safety net.
  const hasOrg = !!user?.organization?.id;
  const { data: roles = [], isLoading: loadingRoles } = useQuery({
    queryKey: ["onboarding-roles"],
    queryFn: orgRoleApi.getAll,
    enabled: hasOrg,
  });

  const updateRow = (i: number, field: keyof InviteRow, value: string) =>
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));

  const handleContinue = () => {
    const filled = rows.filter((r) => r.email.trim() && r.roleId);
    setInvites(filled.map((r) => ({ email: r.email.trim(), roleId: Number(r.roleId) })));
    navigate({ to: "/onboarding/plan" });
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-2">
        <h1 className="font-semibold leading-tight text-(--brand-black)" style={{ fontSize: "2rem" }}>
          {t`Invite your team`}
        </h1>
        <p className="text-sm leading-relaxed text-(--gray-500)">
          {t`Add chefs and kitchen staff to collaborate on recipes and kitchen operations.`}
        </p>
      </div>

      <div className="flex flex-col gap-3">
        {rows.map((row, i) => (
          <div key={i} className="flex gap-3">
            <div
              className="flex-1 flex items-center gap-2.5 h-10 px-3 rounded-md border border-(--gray-200) bg-(--brand-white)"
            >
              <Mail className="w-4 h-4 shrink-0 text-(--gray-400)" />
              <input
                type="email"
                value={row.email}
                onChange={(e) => updateRow(i, "email", e.target.value)}
                placeholder={t`Email address...`}
                className="flex-1 text-sm outline-none bg-transparent text-(--brand-black)"
              />
            </div>

            <select
              value={row.roleId}
              onChange={(e) => updateRow(i, "roleId", e.target.value)}
              disabled={loadingRoles}
              className="h-10 rounded-md px-3 text-sm outline-none transition-shadow appearance-none disabled:opacity-50 border border-(--gray-200) bg-(--brand-white)"
              style={{
                color: row.roleId ? "var(--brand-black)" : "var(--gray-400)",
                minWidth: "10.5rem",
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23afafaf' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
                backgroundRepeat: "no-repeat", backgroundPosition: "right 0.75rem center", paddingRight: "2rem" }}
              onFocus={(e) => { e.currentTarget.style.boxShadow = "0 0 0 3px rgba(109,112,98,0.15)"; e.currentTarget.style.borderColor = "var(--gray-600)"; }}
              onBlur={(e) => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = "var(--gray-200)"; }}
            >
              <option value="" disabled>{t`Select role`}</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>{role.name}</option>
              ))}
            </select>
          </div>
        ))}

        <button
          type="button"
          onClick={() => setRows((prev) => [...prev, { email: "", roleId: "" }])}
          className="flex items-center gap-1.5 text-sm font-medium w-fit transition-opacity hover:opacity-70 text-(--gray-600)"
        >
          <Plus className="w-4 h-4" />
          {t`Add another user`}
        </button>
      </div>

      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={handleContinue}
          className="w-full h-11 flex items-center justify-center gap-2 text-sm font-semibold text-white rounded-md transition-opacity hover:opacity-90 bg-(--brand-black)"
        >
          {t`Continue`} <ArrowRight className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => { setInvites([]); navigate({ to: "/onboarding/plan" }); }}
          className="text-sm font-medium text-center transition-opacity hover:opacity-70 text-(--gray-600)"
        >
          {t`Skip for now`}
        </button>
      </div>
    </div>
  );
}
